import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../store/useStore';
import { leadService } from '../lib/leadService';
import { DueLeadReminder } from '../types/leads.types';
import { APP_ICON } from '../lib/brand';

const AUDIO_PATH = '/sounds/reminder.mp3';
const POLL_INTERVAL_MS = 60 * 1000; // controlla i reminder scaduti ogni minuto
const FIRED_STORAGE_KEY = 'pixel-slab-fired-lead-reminders';
const MAX_STORED_FIRED_IDS = 500;
const SOUND_REPEAT_COUNT = 5; // suona più volte finché non si apre/chiude il toast
// Oltre questa soglia non si apre un toast per reminder: se ne scadono tanti
// insieme diventa un muro di notifiche da chiudere a mano una per una.
const MAX_INDIVIDUAL_TOASTS = 3;

// Reminder già notificati, persistiti così un refresh/riapertura dell'app
// non rifà suonare/apparire in blocco tutti i reminder scaduti da tempo.
const loadFiredIds = (): Set<string> => {
    try {
        const raw = window.localStorage.getItem(FIRED_STORAGE_KEY);
        if (!raw) return new Set();
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? new Set(parsed) : new Set();
    } catch {
        return new Set();
    }
};

const saveFiredIds = (ids: Set<string>) => {
    try {
        const arr = Array.from(ids).slice(-MAX_STORED_FIRED_IDS);
        window.localStorage.setItem(FIRED_STORAGE_KEY, JSON.stringify(arr));
    } catch {
        /* storage non disponibile, non bloccante */
    }
};

const reminderLabel = (rem: DueLeadReminder): string => {
    const leadName = rem.event?.lead?.full_name || rem.event?.lead?.company_name || 'Lead';
    return `${leadName}${rem.event?.title ? ` • ${rem.event.title}` : ''}`;
};

// Chiudere la notifica vale "fatto": il reminder viene completato sul database,
// così non ritorna più su nessun dispositivo. Resta in timeline, segnato verde.
const completeReminders = (ids: string[]) => {
    for (const id of ids) {
        leadService.completeReminder(id).catch(() => { /* non bloccante */ });
    }
};

// Allarme audio + notifica quando un reminder di un lead diventa scaduto.
// Funziona finché l'app è aperta in una scheda del browser (client-side polling).
export const useLeadReminders = () => {
    const { addNotification, markNotificationAsRead, removeNotification } = useNotifications();
    const navigate = useNavigate();
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const firedRef = useRef<Set<string>>(loadFiredIds());
    const stopAlarmRef = useRef<(() => void) | null>(null);

    useEffect(() => {
        const audio = new Audio(AUDIO_PATH);
        audio.preload = 'auto';
        audioRef.current = audio;
        return () => {
            stopAlarmRef.current?.();
        };
    }, []);

    // Sblocca l'audio al primo gesto utente (autoplay policy)
    useEffect(() => {
        const unlock = () => {
            const audio = audioRef.current;
            if (!audio) return;
            audio.play().then(() => { audio.pause(); audio.currentTime = 0; }).catch(() => { /* riprova al trigger */ });
        };
        window.addEventListener('pointerdown', unlock, { once: true });
        window.addEventListener('keydown', unlock, { once: true });
        return () => {
            window.removeEventListener('pointerdown', unlock);
            window.removeEventListener('keydown', unlock);
        };
    }, []);

    useEffect(() => {
        if (typeof window === 'undefined' || !('Notification' in window)) return;
        if (Notification.permission === 'default') {
            Notification.requestPermission().catch(() => { /* ignorato */ });
        }
    }, []);

    useEffect(() => {
        let cancelled = false;

        const stopAlarm = () => {
            const audio = audioRef.current;
            if (!audio) return;
            audio.pause();
            audio.currentTime = 0;
            audio.onended = null;
            stopAlarmRef.current = null;
        };

        const playAlarm = () => {
            const audio = audioRef.current;
            if (!audio) return;
            stopAlarm();
            let remaining = SOUND_REPEAT_COUNT;
            const playOnce = () => {
                remaining -= 1;
                audio.currentTime = 0;
                audio.play().catch(() => { /* gesto utente richiesto */ });
            };
            audio.onended = () => {
                if (remaining > 0) playOnce();
                else stopAlarm();
            };
            stopAlarmRef.current = stopAlarm;
            playOnce();
        };

        const goTo = (path: string, notificationId?: string) => {
            stopAlarm();
            if (notificationId) {
                markNotificationAsRead(notificationId);
                removeNotification(notificationId);
            }
            navigate(path);
        };

        const notifyOs = (title: string, body: string, tag: string, onOpen?: () => void) => {
            if (typeof window === 'undefined' || !('Notification' in window)) return;
            if (Notification.permission !== 'granted') return;
            const osNotif = new Notification(title, {
                body,
                icon: APP_ICON,
                tag,
                requireInteraction: true,
            });
            if (onOpen) {
                osNotif.onclick = () => {
                    window.focus();
                    onOpen();
                    osNotif.close();
                };
            }
        };

        // Un toast per reminder: solo quando ne sono scaduti pochi.
        const fireSingle = (rem: DueLeadReminder) => {
            const leadId = rem.event?.lead_id;
            const message = reminderLabel(rem);
            // Id deterministico così l'action onClick può chiudere proprio questo toast
            const notificationId = `lead-reminder-${rem.id}`;

            notifyOs('Follow-up lead', message, notificationId, leadId ? () => goTo(`/leads/${leadId}`) : undefined);

            addNotification({
                id: notificationId,
                type: 'warning',
                title: 'Follow-up lead',
                message,
                persistent: true,
                onDismiss: () => {
                    stopAlarm();
                    completeReminders([rem.id]);
                },
                action: leadId
                    ? {
                        label: 'Apri lead',
                        onClick: () => {
                            completeReminders([rem.id]);
                            goTo(`/leads/${leadId}`, notificationId);
                        },
                    }
                    : undefined,
            });
        };

        // Un solo toast riassuntivo quando ne scadono molti insieme.
        const fireGrouped = (reminders: DueLeadReminder[]) => {
            const ids = reminders.map((r) => r.id);
            const notificationId = `lead-reminder-group-${ids[0]}`;
            const title = `${reminders.length} follow-up scaduti`;
            const preview = reminders.slice(0, MAX_INDIVIDUAL_TOASTS).map(reminderLabel);
            const rest = reminders.length - preview.length;
            const message = rest > 0 ? `${preview.join('\n')}\n…e altri ${rest}` : preview.join('\n');

            notifyOs(title, message, notificationId, () => goTo('/leads'));

            addNotification({
                id: notificationId,
                type: 'warning',
                title,
                message,
                persistent: true,
                onDismiss: () => {
                    stopAlarm();
                    completeReminders(ids);
                },
                action: {
                    label: 'Apri lead',
                    onClick: () => {
                        completeReminders(ids);
                        goTo('/leads', notificationId);
                    },
                },
            });
        };

        const check = async () => {
            try {
                const due = await leadService.getDueReminders(0);
                if (cancelled) return;

                const fresh = due.filter((rem) => !firedRef.current.has(rem.id));
                if (fresh.length === 0) return;

                for (const rem of fresh) firedRef.current.add(rem.id);
                saveFiredIds(firedRef.current);

                // Un solo allarme per gruppo, non uno per reminder
                playAlarm();

                if (fresh.length <= MAX_INDIVIDUAL_TOASTS) fresh.forEach(fireSingle);
                else fireGrouped(fresh);
            } catch {
                /* ignorato */
            }
        };

        check();
        const intervalId = window.setInterval(check, POLL_INTERVAL_MS);
        return () => {
            cancelled = true;
            window.clearInterval(intervalId);
            stopAlarm();
        };
    }, [addNotification, markNotificationAsRead, removeNotification, navigate]);
};

export default useLeadReminders;
